import React, { useMemo } from 'react';
import { Lock, AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * ProbationTracker — countdown bar + recovery path for an agent on probation.
 *
 * Rendered prominently above the Trust Timeline when the selected
 * agent has an active probation. Conveys three things at a glance:
 *   1. How much time is left (numeric + visual progress)
 *   2. Why probation was triggered (reason text)
 *   3. What recovery looks like (target score + current score gap)
 *
 * Phase A8 / G308.
 *
 * @param {Object} props
 * @param {string} props.startedAt          — ISO timestamp
 * @param {string|null} [props.expiresAt]   — ISO timestamp; null = indefinite
 * @param {string} [props.reason]
 * @param {number} [props.currentScore]
 * @param {number} [props.recoveryThreshold] — score the agent must hit to release
 */
export function ProbationTracker({ startedAt, expiresAt, reason, currentScore, recoveryThreshold }) {
  const { remainingLabel, progressPct, indefinite } = useMemo(() => {
    if (!expiresAt) return { remainingLabel: 'Indefinite', progressPct: null, indefinite: true };
    const end = new Date(expiresAt).getTime();
    const start = startedAt ? new Date(startedAt).getTime() : end - 24 * 3600_000;
    const now = Date.now();
    if (Number.isNaN(end) || Number.isNaN(start)) {
      return { remainingLabel: '—', progressPct: null, indefinite: false };
    }
    if (now >= end) return { remainingLabel: 'Expired', progressPct: 100, indefinite: false };
    const total = Math.max(end - start, 1);
    const elapsed = Math.max(now - start, 0);
    const remaining = end - now;
    const days = Math.floor(remaining / 86_400_000);
    const hours = Math.floor((remaining % 86_400_000) / 3_600_000);
    const minutes = Math.floor((remaining % 3_600_000) / 60_000);
    const label = days > 0
      ? `${days}d ${hours}h remaining`
      : hours > 0
        ? `${hours}h ${minutes}m remaining`
        : `${minutes}m remaining`;
    return { remainingLabel: label, progressPct: Math.min(100, (elapsed / total) * 100), indefinite: false };
  }, [startedAt, expiresAt]);

  const scoreGap = (recoveryThreshold != null && currentScore != null)
    ? Math.round(recoveryThreshold - currentScore)
    : null;
  const onTrack = scoreGap != null && scoreGap <= 0;

  return (
    <div className="bg-v2-danger-soft border-l-4 border-v2-danger rounded-v2-md p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-v2-md bg-v2-danger/15 flex items-center justify-center shrink-0">
          <Lock size={18} className="text-v2-danger-strong" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[14px] font-semibold text-v2-ink m-0 flex items-center gap-2">
            Probation active
            <span className="text-[11.5px] font-normal text-v2-ink-muted">·</span>
            <span className="text-[12px] font-medium text-v2-danger-strong">{remainingLabel}</span>
          </h3>
          <div className="text-[11.5px] text-v2-ink-secondary mt-0.5">
            Started {startedAt ? new Date(startedAt).toLocaleString() : '—'}
            {expiresAt && <> · expires {new Date(expiresAt).toLocaleString()}</>}
          </div>
        </div>
      </div>

      {!indefinite && progressPct != null && (
        <div className="h-1.5 rounded-v2-pill bg-v2-surface/70 overflow-hidden mb-3" aria-hidden="true">
          <div className="h-full bg-v2-danger transition-[width] duration-500"
               style={{ width: `${progressPct.toFixed(1)}%` }} />
        </div>
      )}

      {reason && (
        <div className="bg-v2-surface/70 rounded-v2-sm p-2.5 text-[12px] text-v2-ink mb-2">
          <span className="font-semibold text-v2-ink-secondary text-[10.5px] uppercase tracking-wider block mb-1">
            <AlertTriangle size={11} className="inline -mt-0.5 mr-1" /> Reason
          </span>
          {reason}
        </div>
      )}

      {recoveryThreshold != null && (
        <div className={`rounded-v2-sm p-2.5 text-[12px] flex items-start gap-2 ${
          onTrack ? 'bg-v2-success-soft text-v2-success-strong' : 'bg-v2-surface/70 text-v2-ink'
        }`}>
          {onTrack
            ? <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
            : <span className="shrink-0 mt-0.5 text-v2-warning-strong">→</span>}
          <div>
            <div className="font-semibold text-[11.5px] uppercase tracking-wider">Recovery path</div>
            <div className="mt-0.5">
              {onTrack
                ? `Score recovered (${currentScore} ≥ ${recoveryThreshold}). Operator can release probation.`
                : `Reach trust score ${recoveryThreshold} to release${currentScore != null
                    ? ` — currently ${currentScore} (gap ${scoreGap})` : ''}.`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProbationTracker;
